// src/services/indexeddb.js

const DB_NAME = "trackalyzerDB";
const DB_VERSION = 2;

const TRACKS_STORE_NAME = "tracks";
const TRACKS_INDEX_STORE_NAME = "tracksIndex";
const TRACK_GROUPS_STORE_NAME = "trackGroups";

let db;

/**
 * Opens the IndexedDB database.
 * Creates/upgrades object stores if necessary.
 * @returns {Promise<IDBDatabase>} A promise that resolves with the database instance.
 */
export const openDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const dbInstance = event.target.result;

      if (!dbInstance.objectStoreNames.contains(TRACKS_STORE_NAME)) {
        dbInstance.createObjectStore(TRACKS_STORE_NAME, {
          keyPath: "id",
        });
      }

      if (!dbInstance.objectStoreNames.contains(TRACKS_INDEX_STORE_NAME)) {
        const tracksIndexStore = dbInstance.createObjectStore(
          TRACKS_INDEX_STORE_NAME,
          { keyPath: "id" },
        );
        tracksIndexStore.createIndex("infoName", "info.name", {
          unique: false,
        });
        tracksIndexStore.createIndex(
          "statsDistanceTotal",
          "stats.distance.total",
          { unique: false },
        );
      }

      if (!dbInstance.objectStoreNames.contains(TRACK_GROUPS_STORE_NAME)) {
        dbInstance.createObjectStore(TRACK_GROUPS_STORE_NAME, {
          keyPath: "id",
        });
      }

      console.log("IndexedDB upgrade needed / created object stores");
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      console.log("IndexedDB opened successfully");
      resolve(db);
    };

    request.onerror = (event) => {
      console.error("IndexedDB error:", event.target.errorCode);
      reject(new Error("Failed to open IndexedDB"));
    };
  });
};

/**
 * Adds a track and its index to IndexedDB.
 * @param {object} track - The full track object.
 * @param {object} trackIndex - The flattened track index object.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 */
export const addTrackToDB = async (track, trackIndex) => {
  const database = await openDB();
  const transaction = database.transaction(
    [TRACKS_STORE_NAME, TRACKS_INDEX_STORE_NAME],
    "readwrite",
  );
  const tracksStore = transaction.objectStore(TRACKS_STORE_NAME);
  const tracksIndexStore = transaction.objectStore(TRACKS_INDEX_STORE_NAME);

  return new Promise((resolve, reject) => {
    const requestTrack = tracksStore.add(track);
    const requestTrackIndex = tracksIndexStore.add(trackIndex);

    transaction.oncomplete = () => {
      console.log("Track and index added to DB");
      resolve();
    };
    transaction.onerror = (event) => {
      console.error("Add transaction failed:", event.target.error);
      reject(event.target.error);
    };
    transaction.onabort = (event) => {
      console.warn("Add transaction aborted:", event.target.error);
      reject(new Error("Transaction aborted: " + event.target.error));
    };
  });
};

/**
 * Retrieves all tracks (or their index) from IndexedDB.
 * @param {string} storeName - The name of the object store to retrieve from ('tracks' or 'tracksIndex').
 * @returns {Promise<Array<object>>} A promise that resolves with an array of tracks/indices.
 */
export const getAllFromDB = async (storeName) => {
  const database = await openDB();
  const transaction = database.transaction([storeName], "readonly");
  const store = transaction.objectStore(storeName);
  const request = store.getAll();

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

/**
 * Retrieves a single track from IndexedDB by ID.
 * @param {string} id - The ID of the track to retrieve.
 * @returns {Promise<object|undefined>} A promise that resolves with the track object or undefined if not found.
 */
export const getTrackFromDB = async (id) => {
  const database = await openDB();
  const transaction = database.transaction([TRACKS_STORE_NAME], "readonly");
  const store = transaction.objectStore(TRACKS_STORE_NAME);
  const request = store.get(id);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};

/**
 * Deletes multiple tracks and their indices from IndexedDB by a list of IDs.
 * @param {string[]} ids - An array of IDs of the tracks to delete.
 * @returns {Promise<void>} A promise that resolves when all operations are complete.
 */
export const deleteTracksFromDB = async (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) {
    return Promise.resolve(); // Nothing to delete
  }

  const database = await openDB();
  const transaction = database.transaction(
    [TRACKS_STORE_NAME, TRACKS_INDEX_STORE_NAME],
    "readwrite",
  );
  const tracksStore = transaction.objectStore(TRACKS_STORE_NAME);
  const tracksIndexStore = transaction.objectStore(TRACKS_INDEX_STORE_NAME);

  return new Promise((resolve, reject) => {
    // Collect all delete requests
    const deleteRequests = [];
    ids.forEach((id) => {
      deleteRequests.push(tracksStore.delete(id));
      deleteRequests.push(tracksIndexStore.delete(id));
    });

    // All delete operations are part of the same transaction
    transaction.oncomplete = () => {
      console.log(
        `Successfully deleted ${ids.length} track(s) and their indices from DB.`,
      );
      resolve();
    };
    transaction.onerror = (event) => {
      console.error("Delete transaction failed:", event.target.error);
      reject(event.target.error);
    };
    transaction.onabort = (event) => {
      console.warn("Delete transaction aborted:", event.target.error);
      reject(new Error("Transaction aborted: " + event.target.error));
    };
  });
};

/**
 * Deletes a single track and its index from IndexedDB by ID.
 * This function now just calls deleteTracksFromDB for consistency.
 * @param {string} id - The ID of the track to delete.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 */
export const deleteTrackFromDB = async (id) => {
  return deleteTracksFromDB([id]);
};

export const updateTrackInDB = async (track, trackIndex) => {
  const database = await openDB();
  const transaction = database.transaction(
    [TRACKS_STORE_NAME, TRACKS_INDEX_STORE_NAME],
    "readwrite",
  );
  const tracksStore = transaction.objectStore(TRACKS_STORE_NAME);
  const tracksIndexStore = transaction.objectStore(TRACKS_INDEX_STORE_NAME);

  return new Promise((resolve, reject) => {
    const requestTrack = tracksStore.put(track); // put will add or update
    const requestTrackIndex = tracksIndexStore.put(trackIndex);

    transaction.oncomplete = () => {
      console.log("Track and index updated in DB");
      resolve();
    };
    transaction.onerror = (event) => {
      console.error("Update transaction failed:", event.target.error);
      reject(event.target.error);
    };
    transaction.onabort = (event) => {
      console.warn("Update transaction aborted:", event.target.error);
      reject(new Error("Transaction aborted: " + event.target.error));
    };
  });
};

/**
 * Adds or updates a track group in IndexedDB.
 * @param {object} group - The track group object { id, name }.
 * @returns {Promise<void>}
 */
export const updateTrackGroupInDB = async (group) => {
  const database = await openDB();
  const transaction = database.transaction(
    [TRACK_GROUPS_STORE_NAME],
    "readwrite",
  );
  const store = transaction.objectStore(TRACK_GROUPS_STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.put(group);

    transaction.oncomplete = () => {
      console.log("Track group updated in DB");
      resolve();
    };
    transaction.onerror = (event) => {
      console.error("Group update failed:", event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Deletes a track group from IndexedDB.
 * @param {string} groupId - The ID of the group to delete.
 * @returns {Promise<void>}
 */
export const deleteTrackGroupFromDB = async (groupId) => {
  const database = await openDB();
  const transaction = database.transaction(
    [TRACK_GROUPS_STORE_NAME],
    "readwrite",
  );
  const store = transaction.objectStore(TRACK_GROUPS_STORE_NAME);

  return new Promise((resolve, reject) => {
    const request = store.delete(groupId);

    transaction.oncomplete = () => {
      console.log(`Successfully deleted track group ${groupId} from DB.`);
      resolve();
    };
    transaction.onerror = (event) => {
      console.error("Group delete failed:", event.target.error);
      reject(event.target.error);
    };
  });
};
